// ---------------------------------------------------------------------------
// Shared R2 AI Chat API types
// Used by both backend (NestJS) and frontend clients.
// ---------------------------------------------------------------------------

export enum R2ApiResult
{
    Success = 0,
    FailTokenInvalid = -1,
    FailTokenExpired = -2,
    FailAccessDenied = -3,
    FailServiceTemporaryNotValid = -4,
    FailInvalidParameters = -5,
}


export enum R2CopilotsGetResult
{
    Success = 0,
    FailTokenInvalid = -1,
    FailTokenExpired = -2,
    FailAccessDenied = -3,
    FailServiceTemporaryNotValid = -4,
    FailInvalidParameters = -5,
}


export enum R2SendMessageResult
{
    Success = 0,
    FailTokenInvalid = -1,
    FailTokenExpired = -2,
    FailAccessDenied = -3,
    FailServiceTemporaryNotValid = -4,
    FailInvalidParameters = -5,
    FailServiceMaintenance = -6,
    FailInsufficientBalance = -7,
}


/** Result codes for the upstream-only `/api/msg/send/sync` endpoint. */
export enum R2SendMessageSyncResult
{
    Success = 0,
    FailTokenInvalid = -1,
    FailTokenExpired = -2,
    FailAccessDenied = -3,
    FailServiceTemporaryNotValid = -4,
    FailInvalidParameters = -5,
    FailServiceMaintenance = -6,
    FailInsufficientBalance = -7,
}


export enum R2AudioTranscribeResult
{
    Success = 0,
    FailTokenInvalid = -1,
    FailTokenExpired = -2,
    FailAccessDenied = -3,
    FailServiceTemporaryNotValid = -4,
    FailInvalidParameters = -5,
    FailServiceMaintenance = -6,
    FailInsufficientBalance = -7,
    FailModelProcessFileFormat = -8,
}


export enum R2FileUploadStartResult
{
    Success = 0,
    FailTokenInvalid = -1,
    FailTokenExpired = -2,
    FailAccessDenied = -3,
    FailServiceTemporaryNotValid = -4,
    FailInvalidParameters = -5,
    FailServiceMaintenance = -6,
    FailInsufficientBalance = -7,
    FailFileSize = -8,
    FailFilesStorageLimit = -9,
}


export enum R2FileDownloadStartResult
{
    Success = 0,
    FailTokenInvalid = -1,
    FailTokenExpired = -2,
    FailAccessDenied = -3,
    FailServiceTemporaryNotValid = -4,
    FailInvalidParameters = -5,
    FailServiceMaintenance = -6,
    FailInsufficientBalance = -7,
    FailFileNotFound = -8,
}


export enum R2FilesGetResult
{
    Success = 0,
    FailTokenInvalid = -1,
    FailTokenExpired = -2,
    FailAccessDenied = -3,
    FailServiceTemporaryNotValid = -4,
    FailInvalidParameters = -5,
    FailServiceMaintenance = -6,
    FailInsufficientBalance = -7,
}


export enum R2FileGetResult
{
    SuccessNotFound = 1,
    Success = 0,
    FailTokenInvalid = -1,
    FailTokenExpired = -2,
    FailAccessDenied = -3,
    FailServiceTemporaryNotValid = -4,
    FailInvalidParameters = -5,
    FailServiceMaintenance = -6,
    FailInsufficientBalance = -7,
}


export enum R2FileDeleteResult
{
    Success = 0,
    FailTokenInvalid = -1,
    FailTokenExpired = -2,
    FailAccessDenied = -3,
    FailServiceTemporaryNotValid = -4,
    FailInvalidParameters = -5,
    FailServiceMaintenance = -6,
    FailInsufficientBalance = -7,
}


export enum R2FileCheckResult
{
    Processing = 1,
    Success = 0,
    FailTokenInvalid = -1,
    FailTokenExpired = -2,
    FailAccessDenied = -3,
    FailServiceTemporaryNotValid = -4,
    FailInvalidParameters = -5,
    FailFileNotFound = -6,
    FailOperationNotFound = -7,
    FailOperationExpired = -8,
    FailInvalidContent = -9,
}

export type R2Role = 'USER' | 'ASSISTANT';

export interface IR2Content
{
    text: string;
}

export enum R2MessageMetaType
{
    QuickAction = 0,
    Skill = 1,
}

export interface ContextAttachment
{
    label: string;
    content: string;
    selection_meta?: R2SelectionAttachmentMeta;
}

/** Stable identity and original local input for one root skill invocation. */
export interface IR2SkillInvocationSnapshot
{
    skill_id: number;
    skill_title: string;
    input_text: string;
}

export interface IR2MessageMeta
{
    type?: R2MessageMetaType;
    name?: string;
    original_text?: string;
    history?: boolean;
    attachmentLabels?: ContextAttachment[];
    /** Canonical composer source used to reconstruct interleaved skill chips and text. */
    skill_invocation_source?: string;
    /** Ordered root skills as they appeared in {@link skill_invocation_source}. */
    skill_invocations?: IR2SkillInvocationSnapshot[];
}

// --- Attachments ---

export enum R2AttachmentType
{
    Unknown = -1,
    Image = 0,
    Selection = 1,
    File = 2,
}

export interface R2SelectionAttachmentMeta
{
    /** VS Code URI string for the source document (for example file:///... ). */
    uri: string;
    /** 1-based source range start line. */
    start_line: number;
    /** 1-based source range start column. */
    start_character: number;
    /** 1-based source range end line. */
    end_line: number;
    /** 1-based source range end column. */
    end_character: number;
}

export interface IR2Attachment
{
    type: R2AttachmentType;
    mime_type: string;
    name: string;
    /** Source location metadata for selection attachments. */
    selection_meta?: R2SelectionAttachmentMeta;
    /** Plain text data; present for non-file attachments (type != File). */
    data?: string;
    /** File identifier; present only when type == File. */
    file_id?: string;
    /** File size in bytes; present only when type == File. */
    file_size?: number;
}

export interface IR2ChatHistoryItem
{
    role: R2Role;
    content: IR2Content;
    msg_id: string;
    dt: number;
    meta?: IR2MessageMeta;
    attachments?: IR2Attachment[];
    tokens_usage: number;
    model: string;
}

// --- File metadata ---

export interface IR2File
{
    id: string;
    name: string;
    mime_type: string;
    size: number;
    is_uploaded?: boolean;
    created_dt: number;
}

export interface IR2UserThread
{
    thread_id: string;
    chat_history: IR2ChatHistoryItem[];
}

// --- msg/send ---

export interface IR2Environment
{
    time?: string;
    locale?: string;
    ip?: string;
}

export interface IR2SendMessageRequest
{
    copilot_id: string;
    text: string;
    model: string;
    img_model?: string;
    instruction: string;
    max_bytes_output?: number;
    context_msgs_count?: number;
    thread_id?: string;
    use_web_search?: boolean;
    document?: string;
    meta?: IR2MessageMeta;
    environment?: IR2Environment;
    attachments?: IR2Attachment[];
}

export interface IR2SendMessageResponse
{
    result: R2SendMessageResult;
    thread_id?: string;
    details?: {
        result?: R2SendMessageResult;
    };
}

// --- msgs/get ---

export interface IR2GetMessagesRequest
{
    thread_id: string;
    last_msg_id?: string;
    page_size?: number;
    msg_order?: 'asc' | 'desc';
}

export interface IR2GetMessagesResponse
{
    result: R2ApiResult;
    thread_id?: string;
    chat_history?: IR2ChatHistoryItem[];
}

// --- threads/msgs/get ---

export interface IR2GetThreadsMessagesRequest
{
    msgs_size?: number;
    page_from?: number;
    page_size?: number;
}

export interface IR2GetThreadsMessagesResponse
{
    result: R2ApiResult;
    // if next_page_from equals threads_count_total, it means there is no more data to load
    next_page_from?: number;
    threads_count_total?: number;
    user_threads?: IR2UserThread[];
}

export type DocumentType = 'word' | 'excel' | 'mail' | 'power_point' | 'note';

export interface IR2ThreadDocumentMeta
{
    vendor_id: 'google' | 'microsoft';
    type: DocumentType;
    id: string;
    user_id: string;
}

export interface IR2ThreadCreateIfNotExistsRequest
{
    key: string;
    meta?: IR2ThreadDocumentMeta | Record<string, unknown>;
}

export interface IR2ThreadCreateIfNotExistsResponse
{
    result: R2ApiResult;
    thread_id?: string;
    meta?: IR2ThreadDocumentMeta | Record<string, unknown> | null;
}

export enum R2ThreadIdByKeyGetResult
{
    SuccessNotFound = 1,
    Success = 0,
    FailTokenInvalid = -1,
    FailTokenExpired = -2,
    FailAccessDenied = -3,
    FailServiceTemporaryNotValid = -4,
    FailInvalidParameters = -5,
}

export interface IR2ThreadIdByKeyGetRequest
{
    key: string;
}

export interface IR2ThreadIdByKeyGetResponse
{
    result: R2ThreadIdByKeyGetResult;
    thread_id?: string;
}

export interface IR2ThreadIdAssignRequest
{
    thread_id: string;
    key: string;
    meta?: IR2ThreadDocumentMeta | Record<string, unknown>;
}

export interface IR2ThreadIdAssignResponse
{
    result: R2ApiResult;
    thread_id?: string;
    meta?: IR2ThreadDocumentMeta | Record<string, unknown> | null;
}

export interface IR2ThreadDeleteRequest
{
    thread_ids: string[];
    // Optional copilot identifier. When present, the backend deletes the file
    // attachments of every message in the deleted threads as a background task.
    copilot_id?: string;
}

export interface IR2ThreadDeleteResponse
{
    result: R2ApiResult;
}

export interface IR2ThreadMsgDeleteRequest
{
    thread_id: string;
    msg_id: string;
    copilot_id?: string;
    // Attachments referenced by the message. When present, their backing files
    // are deleted alongside the message on the API side.
    file_ids?: string[];
}

export interface IR2ThreadMsgDeleteResponse
{
    result: R2ApiResult;
}

export interface IR2UserSubscriptionResponse
{
    result: R2ApiResult;
    title?: string;
    user_name?: string;
    dt_from?: number;
    dt_to?: number;
    tokens_left?: number;
    tokens_used?: number;
}

// --- copilots/get ---

export interface IR2Copilot
{
    id: string;
    name: string;
    /** Whether this copilot accepts sell codes via /api/sellcode/apply. */
    is_sellcodes_available?: boolean;
}

export interface IR2GetCopilotsResponse
{
    result: R2CopilotsGetResult;
    copilots?: IR2Copilot[];
}

// --- sellcode/apply ---

export enum R2SellcodeApplyResult
{
    Success = 0,
    FailTokenInvalid = -1,
    FailTokenExpired = -2,
    FailAccessDenied = -3,
    FailServiceTemporaryNotValid = -4,
    FailInvalidParameters = -5,
    FailAlreadyApplied = -6,
    FailExhausted = -7,
    FailInactive = -8,
    FailNotFound = -9,
}

export interface IR2SellcodeApplyRequest
{
    copilot_id: string;
    sellcode: string;
}

export interface IR2SellcodeApplyResponse
{
    result: R2SellcodeApplyResult;
}

// --- key/generate, keys/get, key/delete ---

export enum R2ApiKeyGenerateResult
{
    Success = 0,
    FailTokenInvalid = -1,
    FailTokenExpired = -2,
    FailAccessDenied = -3,
    FailServiceTemporaryNotValid = -4,
    FailInvalidParameters = -5,
    FailServiceMaintenance = -6,
    FailLimitReached = -7,
}

export enum R2ApiKeyDeleteResult
{
    /** The key was already gone; the caller's intent is satisfied. */
    SuccessNotFound = 1,
    Success = 0,
    FailTokenInvalid = -1,
    FailTokenExpired = -2,
    FailAccessDenied = -3,
    FailServiceTemporaryNotValid = -4,
    FailInvalidParameters = -5,
}

export interface IR2ApiKey
{
    name: string;
    copilot_id: string;
    api_key: string;
    expiration_dt: number;
}

export interface IR2ApiKeyGenerateRequest
{
    copilot_id: string;
    name: string;
}

export interface IR2ApiKeyGenerateResponse
{
    result: R2ApiKeyGenerateResult;
    api_key?: string;
    expiration_dt?: number;
}

export interface IR2ApiKeysGetRequest
{
    copilot_id?: string;
    page_from?: number;
    page_size?: number;
    page_order?: 'asc' | 'desc';
}

export interface IR2ApiKeysGetResponse
{
    result: R2ApiResult;
    next_page_from?: number;
    api_keys?: IR2ApiKey[];
}

export interface IR2ApiKeyDeleteRequest
{
    api_key: string;
}

export interface IR2ApiKeyDeleteResponse
{
    result: R2ApiKeyDeleteResult;
}

// --- addin/chat-defaults ---

export interface IR2ChatDefaultsResponse
{
    model: string;
    instruction: string;
    max_bytes_output: number;
}

// --- audio/transcribe ---

export interface IR2AudioTranscribeRequest
{
    copilot_id: string;
    model: string;
    /** Base64-encoded audio bytes, without any `data:` URL prefix. */
    content: string;
    name: string;
    mime_type: string;
}

export interface IR2AudioTranscribeResponse
{
    result: R2AudioTranscribeResult;
    text?: string;
}

// --- file/limits/get ---

export interface IR2FileLimitsResponse
{
    result: R2ApiResult;
    upload_file_size_max?: number;
    upload_storage_files_size_max?: number;
    attachments_count_max?: number;
}

// --- file/upload/start ---

export interface IR2FileUploadStartRequest
{
    copilot_id: string;
    file_name: string;
    mime_type: string;
    file_size: number;
}

export interface IR2FileUploadStartResponse
{
    result: R2FileUploadStartResult;
    operation_id?: number;
    sas_url?: string;
}

// --- file/upload/check ---

export interface IR2FileUploadCheckRequest
{
    operation_id: number;
}

export interface IR2FileUploadCheckResponse
{
    result: R2FileCheckResult;
    file_id?: string;
}

// --- file/download/start ---

export interface IR2FileDownloadStartRequest
{
    copilot_id: string;
    file_id: string;
}

export interface IR2FileDownloadStartResponse
{
    result: R2FileDownloadStartResult;
    operation_id?: number;
}

// --- file/download/check ---

export interface IR2FileDownloadCheckRequest
{
    operation_id: number;
}

export interface IR2FileDownloadCheckResponse
{
    result: R2FileCheckResult;
    sas_url?: string;
}

// --- files/get ---

export interface IR2FilesGetRequest
{
    copilot_id: string;
    cursor?: string;
    page_size?: number;
}

export interface IR2FilesGetResponse
{
    result: R2FilesGetResult;
    files?: IR2File[];
    next_cursor?: string;
}

// --- file/get ---

export interface IR2FileGetRequest
{
    copilot_id: string;
    file_id: string;
}

export interface IR2FileGetResponse
{
    result: R2FileGetResult;
    file?: IR2File;
}

// --- file/delete ---

export interface IR2FileDeleteRequest
{
    copilot_id: string;
    file_ids: string[];
}

export interface IR2FileDeleteResponse
{
    result: R2FileDeleteResult;
}

// --- contacts ---

export interface IR2InlineAttach
{
    name: string;
    original_size: number;
    data_b64: string;
}

export enum R2ContactClient
{
    Web = 'Web',
    OfficeWeb = 'Office Web',
    OfficeDesktop = 'Office Desktop',
    GoogleDocs = 'Google Docs',
    GoogleSheets = 'Google Sheets',
}

// --- contacts/support ---

export interface IR2SupportTicketRequest
{
    client: R2ContactClient;
    copilotId: string;
    message: string;
    inlineAttaches?: IR2InlineAttach[];
}

// --- contacts/report-issue ---
export interface IR2ReportIssueRequest
{
    client: R2ContactClient;
    copilotId: string;
    message: string;
    threadId?: string;
    inlineAttaches?: IR2InlineAttach[];
    promptDetails?: Record<string, unknown>;
    promptResponse?: Record<string, unknown>;
}
